import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '../store/useAuthStore';
import { Chat, useChatStore } from '../store/useChatStore';
import { playMessageNotificationSound, getAlertSettings } from '../utils/turnAlerts';
import { showSystemNotification } from '../utils/systemNotifications';
import { PUSH_NOTIFICATIONS_ENABLED } from '../config/pushNotifications';

const getChatToken = (chat: Chat, userId: string) => {
  const unreadCount = chat.unreadCounts?.[userId] || 0;
  return `${chat.id}:${chat.lastMessageId || chat.lastMessageAt?.seconds || chat.lastMessageAt || ''}:${unreadCount}`;
};

export function useChatMessageAlerts() {
  const navigate = useNavigate();
  const userId = useAuthStore((state) => state.user?.uid);
  const fetchUserProfile = useAuthStore((state) => state.fetchUserProfile);
  const chats = useChatStore((state) => state.chats);
  const knownTokensRef = useRef<Set<string>>(new Set());
  const notifiedTokensRef = useRef<Set<string>>(new Set());
  const bootstrappedForUserRef = useRef<string | null>(null);

  const unreadIncomingChats = useMemo(() => {
    if (!userId) return [];
    return Object.values(chats).filter((chat) => (
      chat.participants?.includes(userId) &&
      chat.lastSenderId &&
      chat.lastSenderId !== userId &&
      (chat.unreadBy?.includes(userId) || (chat.unreadCounts?.[userId] || 0) > 0)
    ));
  }, [chats, userId]);

  useEffect(() => {
    if (!userId) {
      knownTokensRef.current = new Set();
      notifiedTokensRef.current = new Set();
      bootstrappedForUserRef.current = null;
      return;
    }

    if (bootstrappedForUserRef.current !== userId) {
      knownTokensRef.current = new Set(unreadIncomingChats.map((chat) => getChatToken(chat, userId)));
      notifiedTokensRef.current = new Set();
      bootstrappedForUserRef.current = userId;
      return;
    }

  }, [unreadIncomingChats, userId]);

  useEffect(() => {
    if (!userId || bootstrappedForUserRef.current !== userId) return;

    const newChat = unreadIncomingChats.find((chat) => {
      const token = getChatToken(chat, userId);
      return !knownTokensRef.current.has(token) && !notifiedTokensRef.current.has(token);
    });

    unreadIncomingChats.forEach((chat) => {
      knownTokensRef.current.add(getChatToken(chat, userId));
    });

    if (!newChat) return;

    const token = getChatToken(newChat, userId);
    notifiedTokensRef.current.add(token);

    const settings = getAlertSettings();
    if (!settings.chatNotifications) return;

    playMessageNotificationSound();

    const senderId = newChat.lastSenderId;
    const fallbackName = 'un jugador';
    const message = 'Toca para abrir el chat';

    const showToast = (senderName: string) => {
      const chatUrl = newChat.activeGameId ? `/game/${newChat.activeGameId}?chat=1` : '/';
      if (PUSH_NOTIFICATIONS_ENABLED) {
        showSystemNotification({
          kind: 'chat',
          title: `Nuevo mensaje de ${senderName}`,
          body: message,
          url: chatUrl,
          tag: `verbo-chat-${newChat.id}`,
        }).catch(() => {
          // The in-app toast remains the fallback if the platform blocks system notifications.
        });
      }

      toast.message(`Nuevo mensaje de ${senderName}`, {
        description: message,
        duration: 7000,
        action: {
          label: 'Ver chat',
          onClick: () => navigate(chatUrl),
        },
      });
    };

    if (!senderId) {
      showToast(fallbackName);
      return;
    }

    fetchUserProfile(senderId)
      .then((profile) => showToast(profile?.displayName || profile?.nickname || fallbackName))
      .catch(() => showToast(fallbackName));
  }, [fetchUserProfile, navigate, unreadIncomingChats, userId]);
}
